import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MagnifyingGlass, ArrowsClockwise, DownloadSimple, Stack, FileZip, FilePdf, Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";

const LICENSE_OPTIONS = [
  { code: "CABIN_CREW", label: "Kabin Görevlisi" },
  { code: "CABIN_SENIOR", label: "Kıdemli Kabin Görevlisi" },
  { code: "CABIN_SAFETY", label: "Kabin Güvenlik Uzmanı" },
  { code: "SAFETY_OFFICER", label: "Havacılık Güvenlik Yetkilisi" },
  { code: "EMERGENCY_RESPONSE", label: "Acil Müdahale Uzmanı" },
  { code: "FIRE_SAFETY", label: "Yangın Güvenliği ve Kurtarma" },
  { code: "GROUND_STAFF", label: "Yer Hizmetleri Görevlisi" },
  { code: "RAMP_AGENT", label: "Apron / Ramp Görevlisi" },
  { code: "LOAD_CONTROL", label: "Yük Kontrol Yetkilisi" },
  { code: "DG_HANDLING", label: "Tehlikeli Madde Uzmanı" },
  { code: "PASSENGER_SERVICE", label: "Yolcu Hizmetleri Görevlisi" },
];

export default function BulkOperations() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [programs, setPrograms] = useState([]);

  const [licenseType, setLicenseType] = useState("CABIN_CREW");
  const [trainingProgram, setTrainingProgram] = useState("");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [expiryDate, setExpiryDate] = useState("");
  const [modules, setModules] = useState([]);
  const [notes, setNotes] = useState("");

  const [issuing, setIssuing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/students", { params: { q: q || undefined } });
      setStudents(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Öğrenciler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
    api.get("/training-programs").then(({ data }) => setPrograms(data.items || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const txt = q.trim().toLowerCase();
    if (!txt) return students;
    return students.filter((s) =>
      (s.student_no || "").toLowerCase().includes(txt) ||
      (s.full_name || "").toLowerCase().includes(txt) ||
      (s.nationality || "").toLowerCase().includes(txt)
    );
  }, [students, q]);

  const allSelected = filtered.length > 0 && filtered.every((s) => selected.has(s.student_no));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) filtered.forEach((s) => next.delete(s.student_no));
    else filtered.forEach((s) => next.add(s.student_no));
    setSelected(next);
  };
  const toggleOne = (sno) => {
    const next = new Set(selected);
    if (next.has(sno)) next.delete(sno); else next.add(sno);
    setSelected(next);
  };

  const onProgramChange = (e) => {
    const value = e.target.value;
    setTrainingProgram(value);
    const match = programs.find((p) => p.name === value);
    if (match && Array.isArray(match.modules) && match.modules.length > 0) {
      setModules(match.modules.map((m) => ({
        name: m.name,
        hours: String(m.hours),
        completed_date: "",
        description: m.description || "",
        _locked: true,
      })));
    } else if (match && match.default_hours != null && modules.length === 0) {
      setModules([{ name: match.name, hours: String(match.default_hours), completed_date: "" }]);
    } else if (!value) {
      setModules([]);
    }
  };

  const selectedProgram = programs.find((p) => p.name === trainingProgram);
  const curriculumLocked = !!(selectedProgram && selectedProgram.modules && selectedProgram.modules.length > 0);

  const addModule = () => setModules((m) => [...m, { name: "", hours: "", completed_date: "" }]);
  const updateModule = (i, k, v) => setModules((m) => m.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const removeModule = (i) => setModules((m) => m.filter((_, idx) => idx !== i));

  const totalHours = modules.reduce((s, m) => s + (Number(m.hours) || 0), 0).toFixed(1);
  const selectedCount = selected.size;

  const issueForSelected = async () => {
    if (selectedCount === 0) { toast.error("En az bir öğrenci seçin."); return; }
    if (!licenseType || !issueDate || !expiryDate) { toast.error("Lisans türü ve tarihleri doldurun."); return; }
    const cleanModules = modules
      .filter((m) => m.name.trim() && Number(m.hours) > 0)
      .map((m) => ({ name: m.name.trim(), hours: Number(m.hours), completed_date: m.completed_date || null }));
    setIssuing(true);
    try {
      const { data } = await api.post("/certificates/bulk-issue", {
        student_nos: Array.from(selected),
        license_type: licenseType,
        training_program: trainingProgram || null,
        training_modules: cleanModules,
        issue_date: issueDate,
        expiry_date: expiryDate,
        notes: notes || null,
      });
      toast.success(`${data.created_count} sertifika oluşturuldu, ${data.error_count} hata.`);
      loadStudents();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Toplu basım başarısız.");
    } finally {
      setIssuing(false);
    }
  };

  const downloadBulk = async (kind, format, detailed = false) => {
    if (selectedCount === 0) { toast.error("En az bir öğrenci seçin."); return; }
    setDownloading(true);
    try {
      let res;
      let filename;
      if (kind === "certs") {
        const allCerts = [];
        for (const sno of selected) {
          const { data } = await api.get(`/students/${encodeURIComponent(sno)}/certificates`);
          (data || []).forEach((c) => allCerts.push(c.certificate_number));
        }
        if (allCerts.length === 0) { toast.error("Seçili öğrencilerin sertifikası yok."); return; }
        res = await api.post("/certificates/bulk-pdf",
          { certificate_numbers: allCerts, format },
          { responseType: "blob" }
        );
        filename = format === "merged" ? "iava_certificates_merged.pdf" : "iava_certificates.zip";
      } else {
        res = await api.post("/students/bulk-transcript-pdf",
          { student_nos: Array.from(selected), format, mode: "combined", detailed },
          { responseType: "blob" }
        );
        const base = detailed ? "iava_transcripts_detailed" : "iava_transcripts";
        filename = format === "merged" ? `${base}_merged.pdf` : `${base}.zip`;
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("İndirme başladı.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İndirme başarısız.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="bulk-ops-page">
      <span className="label-eyebrow">/ Toplu İşlem</span>
      <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
        Toplu sertifika & transkript
      </h1>
      <p className="text-slate-600 mt-2 max-w-3xl">
        Üst tabloda öğrencileri seçin → altta lisans türü, eğitim programı, modüller ve tarihleri girin →{" "}
        <b>"Seçili öğrencilere sertifika ver"</b>. Aynı seçim ile <b>toplu PDF</b> veya <b>ZIP</b> olarak indirebilirsiniz.{" "}
        Öğrenci yoksa önce <Link to="/admin/students" className="underline text-[#0B2046]">Öğrenciler sayfasından</Link> ekleyin.
      </p>

      {/* Filter bar */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="bulk-ops-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Öğrenci № / ad / uyruk…"
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2046]"
          />
        </div>
        <button onClick={loadStudents} className="px-3 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2 text-sm">
          <ArrowsClockwise size={14} /> Yenile
        </button>
        <div className="ml-auto text-sm font-medium text-slate-700">
          Seçili: <span className="font-mono-plex text-[#0B2046]" data-testid="selected-count">{selectedCount}</span>
        </div>
      </div>

      {/* Student selector table */}
      <div className="mt-4 border border-slate-200 bg-white overflow-x-auto max-h-[40vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 sticky top-0">
            <tr>
              <th className="px-3 py-3 text-left">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all" />
              </th>
              <th className="px-4 py-3 text-left label-eyebrow">Öğrenci №</th>
              <th className="px-4 py-3 text-left label-eyebrow">Ad Soyad</th>
              <th className="px-4 py-3 text-left label-eyebrow">Uyruk</th>
              <th className="px-4 py-3 text-right label-eyebrow">Mevcut Sert.</th>
              <th className="px-4 py-3 text-right label-eyebrow">Toplam Saat</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center label-eyebrow">Yükleniyor…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center label-eyebrow">Öğrenci bulunamadı</td></tr>
            )}
            {filtered.map((s) => (
              <tr key={s.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`bulk-row-${s.student_no}`}>
                <td className="px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={selected.has(s.student_no)}
                    onChange={() => toggleOne(s.student_no)}
                    data-testid={`bulk-check-${s.student_no}`}
                  />
                </td>
                <td className="px-4 py-2.5 font-mono-plex font-bold">{s.student_no}</td>
                <td className="px-4 py-2.5">{s.full_name}</td>
                <td className="px-4 py-2.5">{s.nationality}</td>
                <td className="px-4 py-2.5 text-right font-mono-plex">{s.certificates_count}</td>
                <td className="px-4 py-2.5 text-right font-mono-plex">{s.total_hours}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Issue panel */}
      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-6">
          <div className="label-eyebrow">Adım 2 — Sertifika Bilgileri</div>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">Lisans türü *</label>
              <select value={licenseType} onChange={(e) => setLicenseType(e.target.value)}
                data-testid="bulk-license-type"
                className="w-full px-4 py-3 border border-slate-300 bg-white text-sm">
                {LICENSE_OPTIONS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Eğitim programı</label>
              <select value={trainingProgram} onChange={onProgramChange}
                data-testid="bulk-training-program"
                className="w-full px-4 py-3 border border-slate-300 bg-white text-sm">
                <option value="">— Seçin —</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}{p.default_hours != null ? ` (${p.default_hours} saat)` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Düzenleme tarihi *</label>
              <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)}
                data-testid="bulk-issue-date"
                className="w-full px-4 py-3 border border-slate-300 bg-white text-sm" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Geçerlilik tarihi *</label>
              <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
                data-testid="bulk-expiry-date"
                className="w-full px-4 py-3 border border-slate-300 bg-white text-sm" />
            </div>
          </div>

          <div className="mt-6 border-t border-slate-200 pt-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-eyebrow">Eğitim modülleri</div>
                <p className="text-xs text-slate-500 mt-1">
                  {curriculumLocked
                    ? "Müfredat tanımlı program seçildi — modüller kilitli."
                    : "Her seçili öğrenciye AYNI modüller atanır."}
                </p>
              </div>
              {!curriculumLocked && (
                <button type="button" onClick={addModule} data-testid="bulk-add-module"
                  className="text-sm px-3 py-1.5 border border-slate-300 hover:bg-slate-50">+ Modül ekle</button>
              )}
            </div>
            {modules.length === 0 && (
              <div className="text-xs text-slate-400 italic">Henüz modül eklenmedi.</div>
            )}
            <div className="space-y-2">
              {modules.map((m, i) => (
                <div key={i} className={curriculumLocked ? "bg-slate-50 border border-slate-200 p-2" : ""}>
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <input placeholder="Kurs adı" value={m.name}
                      onChange={(e) => updateModule(i, "name", e.target.value)}
                      data-testid={`bulk-module-name-${i}`}
                      disabled={curriculumLocked}
                      className={`col-span-6 px-3 py-2 border border-slate-300 text-sm ${curriculumLocked ? "bg-slate-100" : "bg-white"}`} />
                    <input type="number" step="0.5" placeholder="Saat" value={m.hours}
                      onChange={(e) => updateModule(i, "hours", e.target.value)}
                      data-testid={`bulk-module-hours-${i}`}
                      disabled={curriculumLocked}
                      className={`col-span-2 px-3 py-2 border border-slate-300 text-sm font-mono-plex ${curriculumLocked ? "bg-slate-100" : "bg-white"}`} />
                    <input type="date" value={m.completed_date}
                      onChange={(e) => updateModule(i, "completed_date", e.target.value)}
                      data-testid={`bulk-module-date-${i}`}
                      className="col-span-3 px-3 py-2 border border-slate-300 text-sm bg-white" />
                    {curriculumLocked ? (
                      <div className="col-span-1 px-2 py-2 text-xs text-emerald-700 text-center">🔒</div>
                    ) : (
                      <button type="button" onClick={() => removeModule(i)}
                        className="col-span-1 px-2 py-2 text-red-600 hover:bg-red-50 text-sm">✕</button>
                    )}
                  </div>
                  {curriculumLocked && m.description && (
                    <div className="mt-1 text-[11px] text-slate-600 italic pl-1">{m.description}</div>
                  )}
                </div>
              ))}
            </div>
            {modules.length > 0 && (
              <div className="mt-3 text-xs font-mono-plex text-slate-600">Toplam saat: {totalHours}</div>
            )}
          </div>

          <div className="mt-5">
            <label className="label-eyebrow block mb-2">Notlar (opsiyonel)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="w-full px-4 py-3 border border-slate-300 bg-white text-sm" />
          </div>
        </div>

        <div className="bg-[#0B2046] text-white p-6 flex flex-col">
          <div className="label-eyebrow text-amber-300">Adım 3 — Toplu Aksiyon</div>
          <div className="mt-2 text-sm text-slate-200">
            <b className="text-2xl font-display font-black">{selectedCount}</b> öğrenci seçildi
          </div>
          <p className="mt-2 text-xs text-slate-300">
            "Sertifika ver" butonu her seçili öğrenciye yukarıdaki bilgilerle BİR sertifika oluşturur.
          </p>
          <button
            onClick={issueForSelected}
            disabled={issuing || selectedCount === 0}
            data-testid="bulk-issue-button"
            className="mt-4 px-4 py-3 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Lightning size={16} weight="fill" /> {issuing ? "Oluşturuluyor…" : "Seçili öğrencilere sertifika ver"}
          </button>

          <div className="border-t border-white/15 mt-6 pt-5">
            <div className="label-eyebrow text-amber-300">Toplu indir</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button onClick={() => downloadBulk("certs", "merged")} disabled={downloading || selectedCount === 0}
                data-testid="bulk-download-certs-merged"
                className="px-3 py-2 bg-white/10 hover:bg-white/20 text-xs inline-flex items-center justify-center gap-1 disabled:opacity-40">
                <FilePdf size={14} /> Sertifika PDF
              </button>
              <button onClick={() => downloadBulk("certs", "zip")} disabled={downloading || selectedCount === 0}
                data-testid="bulk-download-certs-zip"
                className="px-3 py-2 bg-white/10 hover:bg-white/20 text-xs inline-flex items-center justify-center gap-1 disabled:opacity-40">
                <FileZip size={14} /> Sertifika ZIP
              </button>
              <button onClick={() => downloadBulk("transcripts", "merged", false)} disabled={downloading || selectedCount === 0}
                data-testid="bulk-download-trans-merged"
                className="px-3 py-2 bg-white/10 hover:bg-white/20 text-xs inline-flex items-center justify-center gap-1 disabled:opacity-40">
                <FilePdf size={14} /> Transkript PDF
              </button>
              <button onClick={() => downloadBulk("transcripts", "zip", false)} disabled={downloading || selectedCount === 0}
                data-testid="bulk-download-trans-zip"
                className="px-3 py-2 bg-white/10 hover:bg-white/20 text-xs inline-flex items-center justify-center gap-1 disabled:opacity-40">
                <FileZip size={14} /> Transkript ZIP
              </button>
              <button onClick={() => downloadBulk("transcripts", "merged", true)} disabled={downloading || selectedCount === 0}
                data-testid="bulk-download-trans-detailed-merged"
                className="px-3 py-2 bg-amber-400/90 hover:bg-amber-300 text-slate-900 text-xs font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40">
                <FilePdf size={14} /> Detaylı Transkript PDF
              </button>
              <button onClick={() => downloadBulk("transcripts", "zip", true)} disabled={downloading || selectedCount === 0}
                data-testid="bulk-download-trans-detailed-zip"
                className="px-3 py-2 bg-amber-400/90 hover:bg-amber-300 text-slate-900 text-xs font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40">
                <FileZip size={14} /> Detaylı Transkript ZIP
              </button>
            </div>
            <p className="mt-3 text-[11px] text-slate-300">
              <b>Detaylı Transkript</b>: her modülün altında müfredat açıklaması da görünür.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
